import { type InputHTMLAttributes, type ReactNode, type Ref, useEffect, useId, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../cn.ts';
import { useFieldControl } from './FormField.tsx';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label; for a switch without one pass `aria-label`. */
  label?: ReactNode;
  'aria-label'?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** On/off switch, from the fork's components-next/switch/Switch.vue. */
export function Switch({ checked, onCheckedChange, label, disabled, id, className, ...aria }: SwitchProps) {
  const generated = useId();
  const buttonId = id ?? `switch-${generated}`;
  const button = (
    <button
      type="button"
      role="switch"
      id={buttonId}
      aria-checked={checked}
      aria-label={label ? undefined : aria['aria-label']}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'group relative h-4 w-7 shrink-0 rounded-full cursor-pointer select-none transition-colors duration-200 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-slate-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-n-brand' : 'bg-n-slate-6',
        !label && className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-1/2 start-0.5 -translate-y-1/2 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
          checked ? 'translate-x-3 rtl:-translate-x-3' : 'translate-x-0',
        )}
      >
        <span className="block size-3 rounded-full bg-n-background shadow-md" />
      </span>
    </button>
  );
  if (!label) return button;
  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      {button}
      {/* A <label> activates the button it points at, so clicking the text toggles too. */}
      <label htmlFor={buttonId} className="text-body-main text-n-slate-12 cursor-pointer">
        {label}
      </label>
    </div>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  indeterminate?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** Native checkbox with the dashboard look (from components-next/checkbox/Checkbox.vue). */
export function Checkbox({ label, indeterminate = false, className, ref, ...props }: CheckboxProps) {
  const inner = useRef<HTMLInputElement | null>(null);
  const { invalid: _invalid, ...field } = useFieldControl(props);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const box = (
    <span className="relative inline-flex size-4 shrink-0">
      <input
        type="checkbox"
        {...props}
        {...field}
        ref={(node) => {
          inner.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn(
          'peer absolute inset-0 m-0 size-4 cursor-pointer appearance-none rounded border border-n-slate-6 transition-all duration-200',
          'checked:border-n-brand checked:bg-n-brand indeterminate:border-n-brand indeterminate:bg-n-brand',
          'hover:enabled:bg-n-blue-border disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand',
        )}
      />
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-white opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
      />
      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-white opacity-0 peer-indeterminate:opacity-100"
      />
    </span>
  );
  if (!label) return <span className={className}>{box}</span>;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer text-body-main text-n-slate-12',
        className,
      )}
    >
      {box}
      {label}
    </label>
  );
}
